import type { FC, MouseEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  BiData,
  DeviationEditClickBiEvent,
  ClickCopyBiEvent,
  ItemClickBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';
import Checkbox from '@wix/da-ds/pkg/Checkbox';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { getSourceImageRatio } from '@wix/da-shared-react/pkg/DeviationViews/Dumbo/Image/_helpers';
import {
  getDeviationResourceType,
  getDeviationType,
} from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { getIsBoostSupported } from '../../../selectors/studio';
import DeviationThumb from '../../StudioDeviationList/_partials/DeviationThumb';
import { useMainActionDeviationClick } from '../../../hooks/useMainActionDeviationClick';
import ViewButton from '../../tableActions/ViewButton';
import CopyLinkButton from '../../tableActions/CopyLinkButton';
import InlineEditableTitle from '../_partials/InlineEditableTitle';
import EditButton from '../../tableActions/EditButton';
import DeviationActions from '../../tableActions/DeviationActions';
import BoostButton from '../../tableActions/BoostButton';

import s from './StudioDeviationGridItem.scss';

interface Props {
  deviation: PapiDeviation;
  offset: number;
  width: number;
  height: number;
  hasSelection: boolean;
  isSelected: boolean;
  onSelect: (rowIndex: number, isSelected: boolean, isShiftKey?: boolean) => void;
}

const StudioDeviationGridItem: FC<Props> = ({
  deviation,
  offset,
  width,
  height,
  hasSelection,
  isSelected,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isBoostSupported = useSelector(getIsBoostSupported);

  const { onClick, mainAction } = useMainActionDeviationClick(
    deviation,
    containerRef,
    'studio-grid'
  );
  const deviationType = getDeviationType(deviation);
  const imageRatio = getSourceImageRatio(deviation);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      const isOverlayClick = e.target === e.currentTarget;
      if (!isOverlayClick) {
        return;
      } else if (hasSelection) {
        onSelect(offset, !isSelected, e.shiftKey);
      } else {
        onClick(e);
      }
    },
    [onClick, onSelect, offset, isSelected, hasSelection]
  );

  const handleCheckboxClick = useCallback(
    (ev: MouseEvent) => {
      ev.preventDefault();
      onSelect(offset, !isSelected, ev.shiftKey);
    },
    [onSelect, isSelected, offset]
  );

  const handleContextMenuOpen = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleContextMenuClose = useCallback(() => {
    setIsFocused(false);
  }, []);

  // If it's a multi-image deviation we make the thumb squared to have the multiple images shadow effect
  const thumbWidth = deviation.isMultiImage
    ? Math.min(width, Math.floor(height * imageRatio)) - 22
    : width - 16;
  const thumbHeight = deviation.isMultiImage ? height - 10 : height;

  return (
    <div
      className={cn(
        s['root'],
        isSelected && s['selected'],
        hasSelection && s['select-mode'],
        isFocused && s['focused']
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ width }}
      ref={containerRef}
    >
      <div className={s['content']}>
        <div className={cn(s['thumb'], deviation.isMultiImage && s['thumb-multi'])}>
          <DeviationThumb
            deviation={deviation}
            width={thumbWidth}
            height={thumbHeight}
            noCropping={!deviation.isMultiImage}
            largeStudioThumb
            noPadding
            useBackgroundColor={false}
          />
        </div>
        {deviationType !== DeviationTypes.STATUS && (
          <InlineEditableTitle
            deviation={deviation}
            isEditable={isFocused || isHovered}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        )}
      </div>

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={s['overlay']} onClick={handleClick}>
        <Checkbox
          size="big"
          className={s['checkbox']}
          isChecked={isSelected}
          onClick={handleCheckboxClick}
          ariaLabel={t('common.select')}
          originalGreen
        />

        <div className={s['actions']}>
          <DeviationActions
            deviation={deviation}
            offset={offset}
            contextMenuProps={{
              onOpen: handleContextMenuOpen,
              onClose: handleContextMenuClose,
              withIconBackground: true,
            }}
            onlyContextMenu
          />
          {/* main action refers to what happens when the root container is clicked, so we want the opposite here */}
          {mainAction === 'view' ? (
            <EditButton
              className={s['action-button']}
              deviation={deviation}
              variant="icon-with-background"
              biData={BiData<DeviationEditClickBiEvent>({
                evid: 371,
                itemid: deviation.deviationId,
                typeid: getDeviationResourceType(deviation),
                sectionname: BiContextProvidedValue,
              })}
            />
          ) : (
            <ViewButton
              className={s['action-button']}
              deviation={deviation}
              variant="icon-with-background"
              iconSizeOverride={IconSize.SMALL}
              biData={BiData<ItemClickBiEvent>({
                evid: 100,
                itemid: deviation.deviationId,
                typeid: getDeviationResourceType(deviation),
                sectionname: BiContextProvidedValue,
              })}
            />
          )}
          {isBoostSupported ? (
            <BoostButton
              className={s['action-button']}
              deviation={deviation}
              variant="icon-with-background"
            />
          ) : (
            <CopyLinkButton
              className={s['action-button']}
              deviation={deviation}
              variant="icon-with-background"
              biData={BiData<ClickCopyBiEvent>({
                evid: 357,
                typeid: getDeviationResourceType(deviation),
                itemid: deviation.deviationId,
                sectionname: BiContextProvidedValue,
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
};
StudioDeviationGridItem.displayName = 'StudioDeviationGridItem';

export default StudioDeviationGridItem;
